import React, { useState, useRef } from 'react';
import ImageZoomPreviewModal from './ImageZoomPreviewModal';
import {
  isAllowedImageFile,
  prepareImagesForUpload,
} from '../../utils/imageCompression';

const ImageUpload = ({
  selectedImages = [],
  onImagesChange,
  maxImages = 5,
  className = '',
  disabled = false,
  onError,
  autoCompress = false,
  displayImageIndex = null,
  onDisplayImageIndexChange,
  imageIndexOffset = 0,
}) => {
  const [previewUrls, setPreviewUrls] = useState([]);
  const [error, setError] = useState('');
  const [activePreviewIndex, setActivePreviewIndex] = useState(null);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef(null);

  React.useEffect(() => {
    const urls = selectedImages.map((file) => URL.createObjectURL(file));
    setPreviewUrls(urls);

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [selectedImages]);

  const reportError = (msg) => {
    setError(msg);
    if (typeof onError === 'function') onError(msg);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);

    if (files.length === 0) return;

    const invalidFiles = files.filter((file) => !isAllowedImageFile(file));
    if (invalidFiles.length > 0) {
      reportError(
        'Please select only photos (JPEG, PNG, WEBP, HEIC). SVG is not supported.',
      );
      return;
    }

    const preCompressLimit = 25 * 1024 * 1024;
    const oversized = files.filter((file) => file.size > preCompressLimit);
    if (oversized.length > 0) {
      reportError('Each photo must be under 25MB before upload.');
      return;
    }

    const totalImages = selectedImages.length + files.length;
    if (totalImages > maxImages) {
      reportError(`Maximum ${maxImages} images allowed`);
      return;
    }

    setProcessing(true);
    setError('');
    if (typeof onError === 'function') onError('');

    try {
      let nextFiles = files;
      if (autoCompress) {
        const prepared = await prepareImagesForUpload(files);
        nextFiles = prepared.map((item) => item.file);
      } else {
        const postLimit = 10 * 1024 * 1024;
        const tooLarge = nextFiles.filter((file) => file.size > postLimit);
        if (tooLarge.length > 0) {
          reportError('Each file must be under 10MB. Enable compression or choose smaller photos.');
          return;
        }
      }

      onImagesChange([...selectedImages, ...nextFiles]);
      if (
        onDisplayImageIndexChange &&
        displayImageIndex === null &&
        selectedImages.length === 0 &&
        nextFiles.length > 0
      ) {
        onDisplayImageIndexChange(imageIndexOffset);
      }
    } catch (err) {
      reportError(err.message || 'Failed to process photos. Please try again.');
    } finally {
      setProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = (index) => {
    const globalIndex = imageIndexOffset + index;
    const newImages = selectedImages.filter((_, i) => i !== index);
    onImagesChange(newImages);
    setError('');
    if (activePreviewIndex === index) {
      setActivePreviewIndex(null);
    }
    if (onDisplayImageIndexChange && displayImageIndex !== null) {
      if (displayImageIndex === globalIndex) {
        onDisplayImageIndexChange(
          newImages.length > 0 ? imageIndexOffset : null,
        );
      } else if (displayImageIndex > globalIndex) {
        onDisplayImageIndexChange(displayImageIndex - 1);
      }
    }
  };

  const setAsCover = (index) => {
    if (onDisplayImageIndexChange) {
      onDisplayImageIndexChange(imageIndexOffset + index);
    }
  };

  const openFileDialog = () => {
    if (!disabled && !processing) {
      fileInputRef.current?.click();
    }
  };

  const acceptTypes =
    'image/png,image/jpeg,image/jpg,image/gif,image/webp,image/heic,image/heif,.heic,.heif';

  return (
    <div className={`image-upload ${className}`}>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Images ({selectedImages.length}/{maxImages})
        </label>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptTypes}
          onChange={handleFileChange}
          className="hidden"
          disabled={disabled || processing}
        />

        <button
          type="button"
          onClick={openFileDialog}
          disabled={disabled || processing || selectedImages.length >= maxImages}
          className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-600">
            {processing
              ? 'Processing photos…'
              : selectedImages.length >= maxImages
                ? `Maximum ${maxImages} images reached`
                : 'Click to upload photos (iPhone camera supported)'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {autoCompress
              ? 'Photos are optimized automatically (JPEG, PNG, WEBP, HEIC).'
              : 'PNG, JPG, GIF, WEBP up to 10MB each (SVG not supported)'}
          </p>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {previewUrls.length > 0 && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {previewUrls.map((url, index) => {
              const globalIndex = imageIndexOffset + index;
              const isCover = displayImageIndex === globalIndex;
              return (
                <div key={`${url}-${index}`} className="relative group">
                  <button
                    type="button"
                    onClick={() => setActivePreviewIndex(index)}
                    className="block w-full text-left"
                    title="Open zoom preview"
                  >
                    <img
                      src={url}
                      alt={`Preview ${index + 1}`}
                      className={`w-full h-24 object-cover rounded-lg border-2 ${
                        isCover ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                      }`}
                    />
                  </button>
                  {onDisplayImageIndexChange && (
                    <button
                      type="button"
                      onClick={() => setAsCover(index)}
                      disabled={disabled || processing}
                      className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                        isCover
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/90 text-gray-700 hover:bg-white'
                      }`}
                      title="Set as cover image"
                    >
                      {isCover ? 'Cover' : 'Set cover'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    disabled={disabled || processing}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ×
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg truncate">
                    {selectedImages[index]?.name || `Image ${index + 1}`}
                  </div>
                </div>
              );
            })}
          </div>
          {onDisplayImageIndexChange && displayImageIndex === null && selectedImages.length > 0 && (
            <p className="mt-2 text-sm text-amber-600">
              Tip: tap &quot;Set cover&quot; on the photo you want shown on ad cards and search.
            </p>
          )}
        </div>
      )}

      <ImageZoomPreviewModal
        isOpen={activePreviewIndex !== null && activePreviewIndex >= 0}
        imageUrl={activePreviewIndex !== null ? previewUrls[activePreviewIndex] : ''}
        imageName={
          activePreviewIndex !== null ? selectedImages[activePreviewIndex]?.name : ''
        }
        onClose={() => setActivePreviewIndex(null)}
      />
    </div>
  );
};

export default ImageUpload;
